<script setup lang="ts">
const props = defineProps<{
  classBtn?: string | string[] | None
  size: 's' | 'm' | 'l'
  right: boolean | None
  on: boolean
  opened: boolean
  label1: string

  click1: () => void
  click2: () => void
}>()

const big = computed(() => props.size === 'l')

function labelWidth(text: string): number {
  return !text ? 60 : 28 + text.length * 6
}
const labelSize = computed(() => labelWidth(props.label1))

const style = computed(() => {
  const w = labelSize.value
  const add = big.value ? 50 : 30
  return `--pada:${w + add}px; --lw:${w}px`
})

const css = useCssModule()
const cssMain = computed(() => [css.main, props.opened ? 'open' : 'off', props.right ? 'r' : 'l', `s${props.size}`, props.on ? 'saved' : 'new'])
const cssLabel = computed(() => {
  const b = big.value
  const p1 = b ? '3' : '3'
  const p2 = b ? '14' : '04'
  const padding = props.right ? `_pr${p1} _pl${p2}` : `_pr${p2} _pl${p1}`
  return [css.txt2, padding]
})

const button1El = useTemplateRef('button1')
defineExpose({ button1El })
</script>

<template>
  <div ref="mainEl" class="_relative _row _noflex _hcont" :class="cssMain" :style>
    <button type="button" ref="button1" :class="[$style.btn, classBtn]" @click.passive="props.click1">
      <span :class="$style.icon"><slot name="icon" :icon-css="$style.icon2" /></span>

      <span :class="$style.label">
        <span :class="$style.txt">{{ label1 }}</span>
      </span>
    </button>

    <button type="button" :class="$style.test" @click.passive="props.click2">
      <span>
        <slot name="expandable" :cssIcon="$style.cssIcon" :cssLabel />
      </span>
    </button>

    <slot name="after" />
  </div>
</template>

<style module>
.main:global(.open),
:global(.d) .main:hover {
  z-index: 10;
}

/*  */
/*  */
/*  */
/*  */

.cssIcon {
  composes: global(_absolute);
  composes: global(_svg16);
  composes: global(_mta);
  composes: global(_mba);
  top: 0;
  bottom: 0;
}
.main:global(.l) .cssIcon {
  left: 4px;
}
.main:global(.r) .cssIcon {
  right: 4px;
}

/*  */
/*  */
/*  */
/*  */

.test {
  composes: global(_absolute);
  composes: global(_hidden);
  composes: global(_ctr);
  composes: global(_wmax);

  composes: global(_br99);
  composes: global(_bg0);
  composes: global(_b1);
  composes: global(_c1);

  cursor: pointer;
  outline: none;
  z-index: 1;

  top: 4px;
  bottom: 4px;

  opacity: 0.99;
  transition:
    padding 300ms var(--tran2),
    opacity 300ms var(--tran2);
}
:global(.d) .test:hover {
  color: var(--bg);
  background: var(--blue2);
  border-color: var(--blue2);
}
:global(.m) .test,
:global(.d) .main:global(.off):not(:hover) .test {
  transition-delay: 0;
  user-select: none;
  pointer-events: none;
  opacity: 0;
  padding: 0;
}
:global(.d) .main:hover .test,
.main:global(.open) .test {
  transition-delay: 200ms;
}
.main:global(.r) .test {
  left: 10px;
}
.main:global(.l) .test {
  right: 10px;
}
:global(.d) .main:global(.r):hover .test,
.main:global(.open):global(.r) .test {
  padding-left: var(--pada);
}
:global(.d) .main:global(.l):hover .test,
.main:global(.open):global(.l) .test {
  padding-right: var(--pada);
}

/*  */
/*  */
/*  */
/*  */

.btn {
  composes: global(_relative);
  composes: global(_br99);
  composes: global(_b5);
  composes: global(_svg);
  composes: global(_a1);

  cursor: pointer;
  outline: none;
  z-index: 2;

  --svg: 40px;
  --lw: 60px;
}
.main:global(.sl) .btn {
  --svg: 50px;
}
.main:global(.saved) .btn {
  background: var(--blue3);
  border-color: var(--blue3);
}

/*  */
/*  */
/*  */
/*  */

.label {
  composes: global(_absolute);
  composes: global(_hidden);

  composes: global(_br99);
  composes: global(_b5);
  composes: global(_bg0);
  composes: global(_c1);

  composes: global(_pl14);
  composes: global(_pr14);

  composes: global(_flexline);
  composes: global(_ctry);

  top: -1px;
  bottom: -1px;
  z-index: -1;

  transform: scale(0);
  width: calc(var(--svg) + 2px);
  box-shadow:
    1px 2px 0 hsla(240, 4%, 66%, 0.1),
    2px 4px 11px 2px hsla(240, 4%, 80%, 0.1);
  transition:
    width 100ms var(--tran2) 150ms,
    transform 200ms var(--tran2) 300ms,
    box-shadow 200ms var(--tran2);
}
.main:global(.l) .label {
  right: -1px;
}
.main:global(.r) .label {
  left: -1px;
  justify-content: flex-end;
}
.main:global(.saved) .btn .label {
  background: var(--blue3);
  color: var(--bg);
  border-color: var(--blue3);
}
.main:global(.open):global(.new) .label,
:global(.d) .main:global(.new):hover .label {
  border-color: var(--black);
}
:global(.m) .main:global(.new):global(.open) .label,
:global(.d) .main:global(.new) .btn:hover .label {
  background: var(--blue2);
  border-color: var(--blue2);
  color: var(--bg);
}
:global(.d) .main:hover .label,
.main:global(.open) .label {
  width: calc(var(--svg) + var(--lw));
  transform: scale(1);
  transition:
    width 100ms var(--tran2) 200ms,
    transform 200ms var(--tran2) 0ms,
    box-shadow 100ms var(--tran2);
}

/*  */
/*  */
/*  */
/*  */

.icon {
  composes: global(_ctr);
  composes: global(_h100p);
  composes: global(_w100p);
  transition: transform 200ms ease-in;
  transform: scale(0.9);
}
:global(.d) .main:hover .icon,
:global(.m) .main:global(.open) .icon {
  transform: scale(1.02);
}

.icon2 {
  color: currentColor;
  fill: var(--bg);
}

:global(.m) .main:global(.new):global(.open) .icon2,
:global(.d) .main:global(.new) .btn:hover .icon2 {
  color: var(--bg);
  fill: transparent;
}
.main:global(.saved) .icon2 {
  color: var(--bg);
  fill: var(--bg);
}

/*  */
/*  */
/*  */
/*  */

.txt2 {
  composes: global(_wm200);
  composes: global(_ellipsis2);
  composes: global(_hidden);
  composes: global(_row);
  composes: global(_fs08);
  composes: global(_ls-xs);
  composes: global(_fw-xl);
  composes: global(_block);
  composes: global(_noselect);
  line-height: 12px;
  max-height: 24px;
}

/*  */
/*  */
/*  */
/*  */

.txt {
  composes: global(_ws_nowrap);
  composes: global(_ls-xs);
  composes: global(_fw-xl);
  composes: global(_fs09);
  composes: global(_noselect);

  line-height: 12px;
  transition: opacity 100ms var(--tran2) 100ms;
  opacity: 0.01;
}
:global(.d) .btn:hover .txt,
.main:global(.open) .txt,
:global(.d) .main:hover .txt {
  opacity: 0.99;
  transition-delay: 300ms;
}
</style>
